import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createOutletPrinter,
  fetchCurrentDeviceId,
  fetchInstalledPrintersFromDevice,
  fetchOutletPrinters,
  fetchSectionPrinterMappings,
  fetchTakeAwayPrinterMappings,
  getCachedOutletPrinters,
  getCachedSectionMappings,
  getCachedTakeAwayMappings,
  saveSectionPrinterConfiguration,
  saveTakeAwayPrinterConfiguration,
  setCachedOutletPrinters,
  setCachedSectionMappings,
  setCachedTakeAwayMappings,
  testPrinterConnection,
} from '../../services/printerService';
import {
  CreateOutletPrinterDraft,
  CreateOutletPrinterRequest,
  InstalledDevicePrinter,
  OutletPrinter,
  SaveSectionPrinterConfigRequest,
  SaveTakeAwayPrinterConfigRequest,
  SectionPrinterMappingView,
  TakeAwayPrinterMappingView,
} from '../../types/printer';

interface FetchTakeAwayPrinterMappingsInput {
  outletId: string;
  deviceId: string;
}

export const fetchInstalledPrintersThunk = createAsyncThunk<
  InstalledDevicePrinter[],
  void,
  { rejectValue: string }
>('printers/fetchInstalled', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchInstalledPrintersFromDevice();
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch printers');
    }

    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch printers',
    );
  }
});

export const fetchPrinterDeviceIdThunk = createAsyncThunk<
  string,
  void,
  { rejectValue: string }
>('printers/fetchDeviceId', async (_, { rejectWithValue }) => {
  try {
    const response = await fetchCurrentDeviceId();
    if (!response.success || !response.data) {
      return rejectWithValue(
        response.error || 'Failed to resolve current device ID',
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to resolve current device ID',
    );
  }
});

export const fetchOutletPrintersThunk = createAsyncThunk<
  OutletPrinter[],
  string,
  { rejectValue: string }
>('printers/fetchOutletPrinters', async (outletId, { rejectWithValue }) => {
  try {
    const response = await fetchOutletPrinters(outletId);
    if (response.success) {
      const printers = response.data || [];
      await setCachedOutletPrinters(outletId, printers);
      return printers;
    }

    const cached = await getCachedOutletPrinters(outletId);
    if (cached.success && (cached.data || []).length > 0) {
      return cached.data || [];
    }

    return rejectWithValue(
      response.error || 'Failed to fetch outlet printers configuration',
    );
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to fetch outlet printers configuration',
    );
  }
});

export const createOutletPrinterThunk = createAsyncThunk<
  OutletPrinter,
  CreateOutletPrinterRequest,
  { rejectValue: string }
>('printers/createOutletPrinter', async (request, { rejectWithValue }) => {
  try {
    const response = await createOutletPrinter(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to create printer');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create printer',
    );
  }
});

export const testPrinterConnectionThunk = createAsyncThunk<
  boolean,
  CreateOutletPrinterDraft,
  { rejectValue: string }
>('printers/testPrinterConnection', async (request, { rejectWithValue }) => {
  try {
    const response = await testPrinterConnection(request);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to print test page');
    }

    return true;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to print test page',
    );
  }
});

export const fetchSectionPrinterMappingsThunk = createAsyncThunk<
  SectionPrinterMappingView[],
  string,
  { rejectValue: string }
>('printers/fetchSectionMappings', async (outletId, { rejectWithValue }) => {
  try {
    const response = await fetchSectionPrinterMappings(outletId);
    if (response.success) {
      const mappings = response.data || [];
      await setCachedSectionMappings(outletId, mappings);
      return mappings;
    }

    const cached = await getCachedSectionMappings(outletId);
    if (cached.success && (cached.data || []).length > 0) {
      return cached.data || [];
    }

    return rejectWithValue(
      response.error || 'Failed to fetch printer mappings',
    );
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to fetch printer mappings',
    );
  }
});

export const fetchTakeAwayPrinterMappingsThunk = createAsyncThunk<
  TakeAwayPrinterMappingView[],
  FetchTakeAwayPrinterMappingsInput,
  { rejectValue: string }
>(
  'printers/fetchTakeAwayMappings',
  async ({ deviceId, outletId }, { rejectWithValue }) => {
    try {
      const response = await fetchTakeAwayPrinterMappings(outletId, deviceId);
      if (response.success) {
        const mappings = response.data || [];
        await setCachedTakeAwayMappings(outletId, deviceId, mappings);
        return mappings;
      }

      const cached = await getCachedTakeAwayMappings(outletId, deviceId);
      if (cached.success && (cached.data || []).length > 0) {
        return cached.data || [];
      }

      return rejectWithValue(
        response.error || 'Failed to fetch takeaway printer mappings',
      );
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch takeaway printer mappings',
      );
    }
  },
);

export const saveSectionPrinterConfigThunk = createAsyncThunk<
  SectionPrinterMappingView[],
  SaveSectionPrinterConfigRequest,
  { rejectValue: string }
>('printers/saveSectionPrinterConfig', async (request, { rejectWithValue }) => {
  try {
    const saveResponse = await saveSectionPrinterConfiguration(request);
    if (!saveResponse.success) {
      return rejectWithValue(
        saveResponse.error || 'Failed to save section printer configuration',
      );
    }

    const refreshedMappings = await fetchSectionPrinterMappings(
      request.outlet_id,
    );

    if (!refreshedMappings.success) {
      return rejectWithValue(
        refreshedMappings.error ||
          'Saved configuration but failed to refresh mappings',
      );
    }

    const mappings = refreshedMappings.data || [];
    await setCachedSectionMappings(request.outlet_id, mappings);

    return mappings;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to save section printer configuration',
    );
  }
});

export const saveTakeAwayPrinterConfigThunk = createAsyncThunk<
  TakeAwayPrinterMappingView[],
  SaveTakeAwayPrinterConfigRequest,
  { rejectValue: string }
>(
  'printers/saveTakeAwayPrinterConfig',
  async (request, { rejectWithValue }) => {
    try {
      const saveResponse = await saveTakeAwayPrinterConfiguration(request);
      if (!saveResponse.success) {
        return rejectWithValue(
          saveResponse.error || 'Failed to save takeaway printer configuration',
        );
      }

      const refreshedMappings = await fetchTakeAwayPrinterMappings(
        request.outlet_id,
        request.device_id,
      );

      if (!refreshedMappings.success) {
        return rejectWithValue(
          refreshedMappings.error ||
            'Saved configuration but failed to refresh takeaway mappings',
        );
      }

      const mappings = refreshedMappings.data || [];
      await setCachedTakeAwayMappings(
        request.outlet_id,
        request.device_id,
        mappings,
      );

      return mappings;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to save takeaway printer configuration',
      );
    }
  },
);
